import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Z_INDEX_LAYER } from '@CommonConstants';

import { media, BREAK_POINT } from '@Utils/MediaQuery';

const MIN_SIDE_BAR_WIDTH = 220;
const DEFAULT_SIDE_BAR_WIDTH = 240;
const MAX_SIDE_BAR_WIDTH = 305;

const Resizable = ({ children }) => {
  const sidebarRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDE_BAR_WIDTH);

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const getCalcSideBarWidth = useCallback(mouseMoveEvent => {
    const calcSideBarWidth = mouseMoveEvent.clientX - sidebarRef.current.getBoundingClientRect().left;

    if (calcSideBarWidth <= MIN_SIDE_BAR_WIDTH) {
      return MIN_SIDE_BAR_WIDTH;
    }

    if (calcSideBarWidth >= MAX_SIDE_BAR_WIDTH) {
      return MAX_SIDE_BAR_WIDTH;
    }

    return calcSideBarWidth;
  }, []);

  const resize = useCallback(
    mouseMoveEvent => {
      if (isResizing) {
        const calcSideBarWidth = getCalcSideBarWidth(mouseMoveEvent);

        setSidebarWidth(calcSideBarWidth);
      }
    },
    [isResizing],
  );

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);

    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  return (
    <ResizableSideBarContainer ref={sidebarRef} width={sidebarWidth}>
      <ResizableSideBarContent>{children}</ResizableSideBarContent>
      <SideBarResizer onMouseDown={startResizing} />
    </ResizableSideBarContainer>
  );
};

Resizable.propTypes = {
  children: PropTypes.node.isRequired,
};

const ResizableSideBarContainer = styled.div`
  width: ${({ width }) => width}px;
  min-width: ${MIN_SIDE_BAR_WIDTH}px;
  max-width: ${MAX_SIDE_BAR_WIDTH}px;
  height: 100vh;
  min-height: 100vh;

  display: flex;
  flex-grow: 0;
  flex-shrink: 0;

  background-color: ${({ theme }) => theme.colors.ui_2};
  z-index: ${Z_INDEX_LAYER.LEVEL_4};
  overflow-y: scroll;
  ::-webkit-scrollbar {
    display: none;
  }

  ${media.lessThan(BREAK_POINT.XS)`
    width: 180px;
  `}
`;

const ResizableSideBarContent = styled.div`
  flex: 1;

  padding: 16px 6px 16px 16px;
`;

const SideBarResizer = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 10px;

  justify-self: flex-end;
  resize: horizontal;

  cursor: col-resize;
  z-index: ${Z_INDEX_LAYER.LEVEL_5};
`;

export default Resizable;
